import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Copy, Check, LogOut, Shield, Mail, User as UserIcon } from 'lucide-react';
import { useAuth } from '../stores/authStore';
import { TopBar } from '../components/layout/TopBar';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { useNavigate } from 'react-router-dom';

export function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [qr, setQr] = useState<string>('');
  const [copied, setCopied] = useState<string | null>(null);

  const inviteUrl = user
    ? `${window.location.origin}/register?ref=${encodeURIComponent(user.referralCode)}`
    : '';

  useEffect(() => {
    if (!inviteUrl) return;
    QRCode.toDataURL(inviteUrl, {
      margin: 1,
      width: 240,
      color: { dark: '#e51e2a', light: '#ffffff' },
    }).then(setQr).catch(() => setQr(''));
  }, [inviteUrl]);

  function copy(key: string, text: string) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied((s) => (s === key ? null : s)), 1500);
    });
  }

  function onLogout() {
    logout();
    navigate('/login', { replace: true });
  }

  if (!user) return null;

  return (
    <>
      <TopBar title="Profile" />
      <div className="px-4 pt-4 space-y-4">
        {/* Identity */}
        <Card glow>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center text-accent">
              <UserIcon size={28} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-lg font-bold truncate">{user.username}</div>
              <div className="mt-1 flex gap-2">
                <Badge tone="accent">{user.vRank}</Badge>
              </div>
            </div>
          </div>
        </Card>

        {/* Referral */}
        <Card>
          <div className="text-sm font-bold mb-3">Your Referral</div>
          <div className="flex items-center gap-4">
            {qr ? (
              <img src={qr} alt="Referral QR" className="w-28 h-28 rounded-lg border border-border" />
            ) : (
              <div className="w-28 h-28 rounded-lg skeleton" />
            )}
            <div className="flex-1 min-w-0 space-y-2">
              <Row
                label="Code"
                value={user.referralCode}
                copied={copied === 'code'}
                onCopy={() => copy('code', user.referralCode)}
                mono
              />
              <Row
                label="Invite link"
                value={inviteUrl}
                copied={copied === 'url'}
                onCopy={() => copy('url', inviteUrl)}
              />
            </div>
          </div>
        </Card>

        {/* Account */}
        <Card padded={false}>
          <div className="p-4 pb-2 text-sm font-bold">Account</div>
          <div className="divide-y divide-border">
            <InfoRow icon={<Mail size={16} />} label="Email" value={user.email} />
            <InfoRow icon={<Shield size={16} />} label="Member since" value={user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'} />
          </div>
        </Card>

        <Button variant="danger" fullWidth size="lg" onClick={onLogout}>
          <LogOut size={16} /> Sign out
        </Button>
      </div>
    </>
  );
}

function Row({ label, value, mono, copied, onCopy }: { label: string; value: string; mono?: boolean; copied: boolean; onCopy: () => void }) {
  return (
    <div>
      <div className="text-[10px] font-semibold tracking-wider uppercase text-fg-muted">{label}</div>
      <button
        onClick={onCopy}
        className="mt-0.5 w-full flex items-center gap-2 text-left group"
      >
        <span className={`flex-1 min-w-0 truncate text-sm ${mono ? 'font-mono font-bold' : ''}`}>{value}</span>
        <span className="shrink-0 text-fg-muted group-hover:text-fg">
          {copied ? <Check size={15} className="text-success" /> : <Copy size={15} />}
        </span>
      </button>
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="p-3.5 flex items-center gap-3">
      <span className="text-fg-muted">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-fg-muted">{label}</div>
        <div className="text-sm font-semibold truncate">{value}</div>
      </div>
    </div>
  );
}
